import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useSession } from '@/contexts/SessionContext';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { 
  User, MapPin, Briefcase, CreditCard, Phone,
  Save, ArrowLeft, ArrowRight, Users, Camera, X, Upload, Loader2, AlertCircle, GraduationCap
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

// Indian States
const STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

// Districts by State
const DISTRICTS_BY_STATE = {
  'West Bengal': ['Kolkata', 'Howrah', 'North 24 Parganas', 'South 24 Parganas', 'Hooghly', 'Nadia', 'Murshidabad', 'Bardhaman', 'Birbhum', 'Bankura', 'Purulia', 'Medinipur East', 'Medinipur West', 'Jalpaiguri', 'Darjeeling', 'Cooch Behar', 'Malda', 'Dinajpur North', 'Dinajpur South'],
  'Bihar': ['Patna', 'Gaya', 'Muzaffarpur', 'Bhagalpur', 'Darbhanga', 'Purnia', 'Nalanda', 'Vaishali', 'Begusarai', 'Samastipur', 'Munger', 'Buxar', 'Katihar', 'Madhubani'],
  'Jharkhand': ['Ranchi', 'Jamshedpur', 'Dhanbad', 'Bokaro', 'Hazaribagh', 'Deoghar', 'Giridih', 'Dumka', 'Ramgarh'],
  'Delhi': ['Central Delhi', 'East Delhi', 'New Delhi', 'North Delhi', 'North East Delhi', 'North West Delhi', 'Shahdara', 'South Delhi', 'South East Delhi', 'South West Delhi', 'West Delhi'],
  'Maharashtra': ['Mumbai', 'Pune', 'Nagpur', 'Thane', 'Nashik', 'Aurangabad', 'Solapur', 'Kolhapur', 'Sangli', 'Satara'],
  'Uttar Pradesh': ['Lucknow', 'Kanpur', 'Varanasi', 'Agra', 'Prayagraj', 'Meerut', 'Noida', 'Ghaziabad', 'Gorakhpur', 'Mathura'],
  'Other': ['Other']
};

// Qualifications
const QUALIFICATIONS = [
  'Below 10th', '10th Pass', '12th Pass', 'Diploma', 'Graduate (BA/BSc/BCom)',
  'Post Graduate (MA/MSc/MCom)', 'B.Tech/B.E.', 'M.Tech/M.E.', 'MBBS/MD',
  'PhD', 'CA/CS', 'LLB/LLM', 'MBA', 'B.Ed', 'M.Ed', 'Other Professional Degree'
];

const EMPLOYMENT_TYPES = ['permanent', 'contractual', 'temporary', 'probation'];
const SALARY_TYPES = ['monthly', 'daily', 'hourly'];
const SHIFT_TIMINGS = ['Morning (6 AM - 2 PM)', 'Day (9 AM - 5 PM)', 'Evening (2 PM - 10 PM)', 'Night (10 PM - 6 AM)', 'Flexible'];

const StaffFormPage = () => {
  const { getAuthHeader } = useAuth();
  const { checkSessionRequired } = useSession();
  const navigate = useNavigate();
  const { id: staffId } = useParams(); // For edit mode
  const isEditMode = Boolean(staffId);
  
  const [activeTab, setActiveTab] = useState('personal');
  const [loading, setLoading] = useState(false);
  const [fetchingData, setFetchingData] = useState(false);
  const [categories, setCategories] = useState({});
  const [designations, setDesignations] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    // Personal
    name: '',
    email: '',
    phone: '',
    alternate_phone: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    aadhaar_number: '',
    pan_number: '',
    
    // Address
    present_address: '',
    permanent_address: '',
    city: '',
    district: '',
    state: 'West Bengal',
    pin_code: '',
    same_address: false,
    
    // Employment
    category: '',
    designation: '',
    department: '',
    joining_date: new Date().toISOString().split('T')[0],
    employment_type: 'permanent',
    shift_timing: '',
    education: '',
    skills: '',
    total_experience_years: '',
    previous_employer: '',
    
    // Salary & Bank
    salary_type: 'monthly',
    basic_salary: '',
    bank_name: '',
    account_number: '',
    ifsc_code: '',
    pf_number: '',
    esi_number: '',
    
    // Emergency Contact
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
  });

  useEffect(() => {
    fetchCategories();
    if (isEditMode) {
      fetchStaffData();
    }
  }, [staffId]);

  const fetchStaffData = async () => {
    setFetchingData(true);
    try {
      const res = await axios.get(`${API_URL}/api/staff/${staffId}`, { headers: getAuthHeader() });
      const staff = res.data;
      
      // Map API response to form data
      setFormData({
        name: staff.name || '',
        email: staff.email || '',
        phone: staff.phone || '',
        alternate_phone: staff.alternate_phone || '',
        date_of_birth: staff.date_of_birth || '',
        gender: staff.gender || '',
        blood_group: staff.blood_group || '',
        aadhaar_number: staff.aadhaar_number || '',
        pan_number: staff.pan_number || '',
        present_address: staff.present_address || staff.address || '',
        permanent_address: staff.permanent_address || '',
        city: staff.city || '',
        district: staff.district || '',
        state: staff.state || 'West Bengal',
        pin_code: staff.pin_code || '',
        same_address: staff.same_address || false,
        category: staff.category || '',
        designation: staff.designation || '',
        department: staff.department || '',
        joining_date: staff.joining_date || '',
        employment_type: staff.employment_type || 'permanent',
        shift_timing: staff.shift_timing || '',
        education: staff.education || '',
        skills: staff.skills || '',
        total_experience_years: staff.total_experience_years || '',
        previous_employer: staff.previous_employer || '',
        salary_type: staff.salary_type || 'monthly',
        basic_salary: staff.basic_salary || staff.salary || '',
        bank_name: staff.bank_name || '',
        account_number: staff.account_number || '',
        ifsc_code: staff.ifsc_code || '',
        pf_number: staff.pf_number || '',
        esi_number: staff.esi_number || '',
        emergency_contact_name: staff.emergency_contact_name || '',
        emergency_contact_phone: staff.emergency_contact_phone || '',
        emergency_contact_relation: staff.emergency_contact_relation || '',
      });
      
      // Update designations based on category
      if (staff.category && categories[staff.category]) {
        setDesignations(categories[staff.category].designations || []);
      }
      
      // Fetch photo from documents
      try {
        const docsRes = await axios.get(`${API_URL}/api/documents/staff/staff/${staffId}`, { headers: getAuthHeader() });
        const docs = docsRes.data.documents || [];
        // Get latest photo (sort by uploaded_at descending)
        const photos = docs.filter(d => d.document_type === 'staff_photo')
          .sort((a, b) => new Date(b.uploaded_at) - new Date(a.uploaded_at));
        const photoDoc = photos[0];
        if (photoDoc?.cloudinary_url) {
          setPhotoPreview(photoDoc.cloudinary_url);
        } else if (photoDoc?.file_path && photoDoc.file_path.startsWith('http')) {
          setPhotoPreview(photoDoc.file_path);
        }
      } catch (docError) {
        console.error('Failed to fetch photo:', docError);
      }
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      toast.error('Failed to load staff data');
    } finally {
      setFetchingData(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/staff/categories`, { headers: getAuthHeader() });
      setCategories(res.data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (100 KB max)
      if (file.size > 100 * 1024) {
        toast.error('Photo size must be less than 100 KB');
        e.target.value = '';
        return;
      }
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        e.target.value = '';
        return;
      }
      
      // Show preview immediately
      setPhotoPreview(URL.createObjectURL(file));
      
      // If editing existing staff, upload immediately
      if (isEditMode && staffId) {
        try {
          const uploadFormData = new FormData();
          uploadFormData.append('file', file);
          uploadFormData.append('document_type', 'staff_photo');
          
          await axios.post(
            `${API_URL}/api/documents/staff/upload/staff/${staffId}`,
            uploadFormData,
            { headers: { ...getAuthHeader(), 'Content-Type': 'multipart/form-data' } }
          );
          toast.success('Photo uploaded successfully!');
        } catch (error) {
          toast.error('Photo upload failed');
          setPhotoPreview(null);
        }
      } else {
        // For new staff, store file to upload after save
        setPhotoFile(file);
      }
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  const uploadPhoto = async (staffId) => {
    if (!photoFile) return;
    
    try {
      const formData = new FormData();
      formData.append('file', photoFile);
      formData.append('document_type', 'staff_photo');
      
      await axios.post(
        `${API_URL}/api/documents/staff/upload/staff/${staffId}`,
        formData,
        { 
          headers: { 
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Photo uploaded successfully!');
    } catch (error) {
      console.error('Photo upload failed:', error);
      toast.error('Photo upload failed. You can upload later from Staff page.');
    }
  };

  const handleCategoryChange = (category) => {
    setFormData(prev => ({ ...prev, category, designation: '' }));
    setDesignations(categories[category]?.designations || []);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'same_address' && value) {
        updated.permanent_address = prev.present_address;
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.phone || !formData.category || !formData.designation || !formData.joining_date) {
      toast.error('Please fill required fields: Name, Phone, Category, Designation, Joining Date');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        total_experience_years: formData.total_experience_years ? parseFloat(formData.total_experience_years) : null,
        basic_salary: formData.basic_salary ? parseFloat(formData.basic_salary) : null,
      };
      
      let staffIdResult;
      
      if (isEditMode) {
        // Update existing staff
        await axios.put(`${API_URL}/api/staff/${staffId}`, payload, { headers: getAuthHeader() });
        staffIdResult = staffId;
        toast.success('Staff updated successfully!');
      } else {
        // Create new staff
        const res = await axios.post(`${API_URL}/api/staff`, payload, { headers: getAuthHeader() });
        staffIdResult = res.data.id;
        toast.success(`Staff added successfully! ID: ${res.data.employee_id || res.data.id?.slice(0,8)}`);
      }
      
      // Upload photo if selected
      if (photoFile && staffIdResult) {
        await uploadPhoto(staffIdResult);
      }
      
      navigate('/staff');
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${isEditMode ? 'update' : 'add'} staff`);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'personal', label: 'Personal', icon: User },
    { id: 'address', label: 'Address', icon: MapPin },
    { id: 'employment', label: 'Employment', icon: Briefcase },
    { id: 'salary', label: 'Salary & Bank', icon: CreditCard },
    { id: 'emergency', label: 'Emergency', icon: Phone },
  ];

  const currentTabIndex = tabs.findIndex(t => t.id === activeTab);
  const progress = ((currentTabIndex + 1) / tabs.length) * 100;

  // Check if session is set
  const sessionCheck = checkSessionRequired();

  if (fetchingData) {
    return (
      <DashboardLayout title="Loading...">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  // Show session required message for new entries
  if (!isEditMode && !sessionCheck.allowed) {
    return (
      <DashboardLayout title="Add New Staff">
        <div className="max-w-2xl mx-auto mt-10">
          <Alert variant="destructive">
            <AlertCircle className="h-5 w-5" />
            <AlertTitle>Session Not Set!</AlertTitle>
            <AlertDescription className="mt-2">
              {sessionCheck.message}
            </AlertDescription>
          </Alert>
          <div className="flex gap-4 mt-6">
            <Button variant="outline" onClick={() => navigate('/staff')}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Staff
            </Button>
            <Button onClick={() => navigate('/academic')}>
              <GraduationCap className="h-4 w-4 mr-2" /> Go to Academic Settings
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const goToNextTab = () => {
    const nextIndex = currentTabIndex + 1;
    if (nextIndex < tabs.length) {
      setActiveTab(tabs[nextIndex].id);
    }
  };

  const goToPrevTab = () => {
    const prevIndex = currentTabIndex - 1;
    if (prevIndex >= 0) {
      setActiveTab(tabs[prevIndex].id);
    }
  };

  return (
    <DashboardLayout title={isEditMode ? "Edit Staff" : "Add New Staff"}>
      <div className="max-w-4xl mx-auto space-y-4">
        {/* Back Button */}
        <Button variant="outline" onClick={() => navigate('/staff')} className="mb-2">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Staff
        </Button>
        
        <div className="flex items-center gap-4">
          <Progress value={progress} className="flex-1" />
          <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {isEditMode ? 'Edit Staff' : 'Non-Teaching Staff Registration Form'}
            </CardTitle>
            <CardDescription>Fill all required details for staff registration</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-5 mb-6">
                {tabs.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <TabsTrigger key={tab.id} value={tab.id} className="text-xs">
                      <Icon className="h-4 w-4 mr-1 hidden sm:inline" />
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Personal Information */}
              <TabsContent value="personal" className="space-y-4">
                {/* Photo Upload Section */}
                <div className="flex items-start gap-6 p-4 border rounded-lg bg-muted/30">
                  <div className="flex flex-col items-center">
                    <div className="relative w-32 h-32 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-background">
                      {photoPreview ? (
                        <>
                          <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute top-1 right-1 h-6 w-6"
                            onClick={removePhoto}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <Camera className="h-8 w-8 text-muted-foreground/50" />
                      )}
                    </div>
                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handlePhotoChange}
                      className="hidden"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      onClick={() => photoInputRef.current?.click()}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      {photoPreview ? 'Change' : 'Upload Photo'}
                    </Button>
                  </div>
                  <div className="flex-1 text-sm text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">Staff Photo</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Passport size photo preferred</li>
                      <li>Max size: 100 KB</li>
                      <li>Formats: JPG, PNG, WebP</li>
                      <li>Photo will be used for ID Card</li>
                    </ul>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Full Name *</Label>
                    <Input value={formData.name} onChange={e => handleInputChange('name', e.target.value)} placeholder="As per documents" />
                  </div>
                  <div>
                    <Label>Mobile Number * (10 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.phone} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange('phone', val);
                      }}
                      maxLength={10}
                      placeholder="10 digit mobile number"
                    />
                  </div>
                  <div>
                    <Label>Alternate Mobile (10 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.alternate_phone} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange('alternate_phone', val);
                      }}
                      maxLength={10}
                      placeholder="10 digit mobile number"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={formData.email} onChange={e => handleInputChange('email', e.target.value)} />
                  </div>
                  <div>
                    <Label>Date of Birth</Label>
                    <Input type="date" value={formData.date_of_birth} onChange={e => handleInputChange('date_of_birth', e.target.value)} />
                  </div>
                  <div>
                    <Label>Gender</Label>
                    <Select value={formData.gender} onValueChange={v => handleInputChange('gender', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Blood Group</Label>
                    <Select value={formData.blood_group} onValueChange={v => handleInputChange('blood_group', v)}>
                      <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {BLOOD_GROUPS.map(bg => <SelectItem key={bg} value={bg}>{bg}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Aadhaar Number (12 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.aadhaar_number} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                        handleInputChange('aadhaar_number', val);
                      }}
                      maxLength={12}
                      placeholder="12 digit Aadhaar"
                    />
                  </div>
                  <div>
                    <Label>PAN Number</Label>
                    <Input value={formData.pan_number} onChange={e => handleInputChange('pan_number', e.target.value)} maxLength={10} placeholder="ABCDE1234F" />
                  </div>
                </div>
              </TabsContent>

              {/* Address */}
              <TabsContent value="address" className="space-y-4">
                <div className="space-y-4">
                  <div>
                    <Label>Present Address</Label>
                    <Textarea 
                      value={formData.present_address} 
                      onChange={e => handleInputChange('present_address', e.target.value)} 
                      rows={3}
                      placeholder="House No, Street, Area..."
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input 
                      type="checkbox" 
                      id="same_address"
                      checked={formData.same_address}
                      onChange={e => handleInputChange('same_address', e.target.checked)}
                      className="rounded"
                    />
                    <Label htmlFor="same_address" className="cursor-pointer">Permanent address same as present</Label>
                  </div>
                  <div>
                    <Label>Permanent Address</Label>
                    <Textarea 
                      value={formData.permanent_address} 
                      onChange={e => handleInputChange('permanent_address', e.target.value)} 
                      rows={3}
                      disabled={formData.same_address}
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input value={formData.city} onChange={e => handleInputChange('city', e.target.value)} />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Select value={formData.state} onValueChange={v => { handleInputChange('state', v); handleInputChange('district', ''); }}>
                        <SelectTrigger><SelectValue placeholder="Select State" /></SelectTrigger>
                        <SelectContent>
                          {STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>District</Label>
                      <Select value={formData.district} onValueChange={v => handleInputChange('district', v)} disabled={!formData.state}>
                        <SelectTrigger><SelectValue placeholder={formData.state ? "Select District" : "Select State first"} /></SelectTrigger>
                        <SelectContent>
                          {(DISTRICTS_BY_STATE[formData.state] || DISTRICTS_BY_STATE['Other'] || []).map(d => (
                            <SelectItem key={d} value={d}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>PIN Code (6 digits)</Label>
                      <Input 
                        type="tel"
                        value={formData.pin_code} 
                        onChange={e => {
                          const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                          handleInputChange('pin_code', val);
                        }}
                        maxLength={6}
                        placeholder="6 digit PIN"
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Employment Details */}
              <TabsContent value="employment" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Category *</Label>
                    <Select value={formData.category} onValueChange={handleCategoryChange}>
                      <SelectTrigger><SelectValue placeholder="Select Category" /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(categories).map(([key, val]) => (
                          <SelectItem key={key} value={key}>{val.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Designation *</Label>
                    <Select value={formData.designation} onValueChange={v => handleInputChange('designation', v)} disabled={!formData.category}>
                      <SelectTrigger><SelectValue placeholder="Select Designation" /></SelectTrigger>
                      <SelectContent>
                        {designations.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Department</Label>
                    <Input value={formData.department} onChange={e => handleInputChange('department', e.target.value)} placeholder="e.g., Administration, Accounts" />
                  </div>
                  <div>
                    <Label>Joining Date *</Label>
                    <Input type="date" value={formData.joining_date} onChange={e => handleInputChange('joining_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>Employment Type</Label>
                    <Select value={formData.employment_type} onValueChange={v => handleInputChange('employment_type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENT_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Shift Timing</Label>
                    <Select value={formData.shift_timing} onValueChange={v => handleInputChange('shift_timing', v)}>
                      <SelectTrigger><SelectValue placeholder="Select Shift" /></SelectTrigger>
                      <SelectContent>
                        {SHIFT_TIMINGS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Education</Label>
                    <Input value={formData.education} onChange={e => handleInputChange('education', e.target.value)} placeholder="e.g., 10th Pass, Graduate" />
                  </div>
                  <div>
                    <Label>Total Experience (Years)</Label>
                    <Input type="number" value={formData.total_experience_years} onChange={e => handleInputChange('total_experience_years', e.target.value)} min={0} step={0.5} />
                  </div>
                  <div>
                    <Label>Previous Employer</Label>
                    <Input value={formData.previous_employer} onChange={e => handleInputChange('previous_employer', e.target.value)} />
                  </div>
                  <div>
                    <Label>Skills</Label>
                    <Input value={formData.skills} onChange={e => handleInputChange('skills', e.target.value)} placeholder="e.g., Driving, Cooking, Accounts" />
                  </div>
                </div>
              </TabsContent>

              {/* Salary & Bank */}
              <TabsContent value="salary" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Salary Type</Label>
                    <Select value={formData.salary_type} onValueChange={v => handleInputChange('salary_type', v)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SALARY_TYPES.map(t => <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Basic Salary (₹)</Label>
                    <Input type="number" value={formData.basic_salary} onChange={e => handleInputChange('basic_salary', e.target.value)} placeholder="0.00" />
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <h4 className="font-medium mb-4">Bank Details</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Bank Name</Label>
                      <Input value={formData.bank_name} onChange={e => handleInputChange('bank_name', e.target.value)} placeholder="e.g., State Bank of India" />
                    </div>
                    <div>
                      <Label>Account Number</Label>
                      <Input value={formData.account_number} onChange={e => handleInputChange('account_number', e.target.value)} />
                    </div>
                    <div>
                      <Label>IFSC Code</Label>
                      <Input value={formData.ifsc_code} onChange={e => handleInputChange('ifsc_code', e.target.value)} placeholder="SBIN0001234" />
                    </div>
                    <div>
                      <Label>PF Number (if any)</Label>
                      <Input value={formData.pf_number} onChange={e => handleInputChange('pf_number', e.target.value)} />
                    </div>
                    <div>
                      <Label>ESI Number (if any)</Label>
                      <Input value={formData.esi_number} onChange={e => handleInputChange('esi_number', e.target.value)} />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* Emergency Contact */}
              <TabsContent value="emergency" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Emergency Contact Name</Label>
                    <Input value={formData.emergency_contact_name} onChange={e => handleInputChange('emergency_contact_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Emergency Contact Phone (10 digits)</Label>
                    <Input 
                      type="tel"
                      value={formData.emergency_contact_phone} 
                      onChange={e => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 10);
                        handleInputChange('emergency_contact_phone', val);
                      }}
                      maxLength={10}
                      placeholder="10 digit mobile number"
                    />
                  </div>
                  <div>
                    <Label>Relation</Label>
                    <Select value={formData.emergency_contact_relation} onValueChange={v => handleInputChange('emergency_contact_relation', v)}>
                      <SelectTrigger><SelectValue placeholder="Select Relation" /></SelectTrigger>
                      <SelectContent>
                        {['Father', 'Mother', 'Wife', 'Husband', 'Brother', 'Sister', 'Son', 'Daughter', 'Friend', 'Other'].map(r => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-muted/50 p-4 rounded-lg mt-6">
                  <p className="text-sm text-muted-foreground">
                    📄 <strong>Note:</strong> You can upload additional documents (Aadhaar, PAN, Resume, etc.) 
                    from the Staff list page by clicking on &quot;View Details&quot; → &quot;Documents&quot; tab.
                  </p>
                </div>
              </TabsContent>

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-6 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={goToPrevTab}
                  disabled={currentTabIndex === 0}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Previous
                </Button>

                {currentTabIndex === tabs.length - 1 ? (
                  <Button onClick={handleSubmit} disabled={loading}>
                    <Save className="h-4 w-4 mr-2" />
                    {loading ? 'Saving...' : 'Add Staff Member'}
                  </Button>
                ) : (
                  <Button onClick={goToNextTab}>
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                )}
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default StaffFormPage;
